import { useMemo, useState, useEffect, useRef } from "react";
import styles from "../styles/pages/Mails.module.css";

import MailsTable from "../components/mails/MailsTable";
import MailsToolbar from "../components/mails/MailsToolbar";
import MailErrorsTable from "../components/mails/MailErrorsTable";
import { EMPTY_MAIL_FILTERS } from "../components/mails/MailsFindFrame";
import MailFlyer from "../components/mails/MailFlyer";
import Button from "../ui/buttons/Button";
import IconButton from "../ui/buttons/IconButton";
import MailPreview from "../components/mails/MailPreview";
import NewMailModal from "../components/mails/NewMailModal";
import Toggle from "../ui/selectors/Toggle";

import rocket from "../assets/icons/rocket.svg";
import rocketHover from "../assets/icons/rocket-hover.svg";

import { ReactComponent as TrashIcon } from "../assets/icons/trash.svg";
import backIcon from "../assets/icons/leftArrow.svg";

import { mailsService } from "../services/mails.service";
import { leadsService } from "../services/leads.service";
import {
  buildEnabledMailChanges,
  buildMailEnableSummaryWarning,
  mailNeedsEnabling,
} from "../utils/mailEnabling";
import FloatingModal from "../ui/Modals/FloatingModal";
import LoadingScreen from "../ui/loadingScreen/LoadingScreen";
import { ConfirmDeleteModal } from "../components/admin/AdminDetailModals";

function HoverRocketButton({
  children,
  width,
  height,
  tone = "green",
  disabled = false,
  onClick,
}) {
  const [hovered, setHovered] = useState(false);

  const rocketIcon = (
    <img
      src={hovered ? rocketHover : rocket}
      alt=""
      style={{ width: 32, height: 32, display: "block" }}
    />
  );

  return (
    <Button
      variant="special"
      tone={tone}
      width={width}
      height={height}
      iconRight={rocketIcon}
      disabled={disabled}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      labelStyle={{ fontWeight: 500 }}
    >
      {children}
    </Button>
  );
}

const BackIcon = (
  <img
    src={backIcon}
    alt=""
    className={styles.backIcon}
    style={{ width: 16, height: 16, display: "block" }}
  />
);

function isReGetTemplateUpdated(result) {
  return Boolean(result.ok && result.updated);
}

function normalizeSearchText(text) {
  return String(text ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

// Concatena todos los campos "buscables" de una fila del listado de template
// groups: cuenta, template, tipo, cantidad de mails, estado y tasa de
// respuesta.
function getTemplateGroupSearchText(row) {
  const lateValue = row?.late ?? 0;
  const statusText = lateValue > 15 ? `${lateValue}% late` : "up to date";

  const searchableValues = [
    row?.account?.name,
    row?.group?.name,
    row?.type?.name,
    row?.mails_count,
    statusText,
    row?.response_rate != null && row?.response_rate !== ""
      ? `${row.response_rate}%`
      : null,
  ];

  return normalizeSearchText(
    searchableValues.filter((value) => value != null).join(" "),
  );
}

function matchesEveryToken(searchText, tokens) {
  return tokens.every((token) => searchText.includes(token));
}

const MailsPage = () => {
  const [fetchingMy] = useState();
  const [lista, setLista] = useState([]);
  const [mails, setMails] = useState([]);
  const [filteredMails, setFilteredMails] = useState([]);

  const [viewMode, setViewMode] = useState("list");

  const [mailItems, setMailItems] = useState(null);
  const [selectedTemplateGroup, setSelectedTemplateGroup] = useState(null);
  const [showMailInfo, setShowMailInfo] = useState(false);
  const [lateralModalData, setLateralModalData] = useState({});

  const mailItemsQueryRef = useRef(0);
  const mailItemsPageRef = useRef(1);
  const mailItemsGroupIdRef = useRef(null);
  const mailsScrollStateRef = useRef({});
  const [hasMoreMailItems, setHasMoreMailItems] = useState(false);
  const [isLoadingMoreMailItems, setIsLoadingMoreMailItems] = useState(false);
  const [isLoadingMailItems, setIsLoadingMailItems] = useState(false);
  const [mailItemsSearchTerm, setMailItemsSearchTerm] = useState("");

  const [mailFilters, setMailFilters] = useState(EMPTY_MAIL_FILTERS);
  const [mailAccountOptions, setMailAccountOptions] = useState([]);
  const seenResponsiblesRef = useRef(new Map());

  const errorsQueryRef = useRef(0);
  const errorsPageRef = useRef(1);
  const [errorRows, setErrorRows] = useState([]);
  const [hasMoreErrors, setHasMoreErrors] = useState(false);
  const [isLoadingErrors, setIsLoadingErrors] = useState(false);
  const [isLoadingMoreErrors, setIsLoadingMoreErrors] = useState(false);
  const [errorsLoadFailed, setErrorsLoadFailed] = useState(false);
  const [errorsSearchTerm, setErrorsSearchTerm] = useState("");

  const [newMailModalState, setNewMailModalState] = useState(false);
  const [newMailError, setNewMailError] = useState("");
  const [leads, setLeads] = useState();

  const mailPreviewQueryRef = useRef(0);
  const [isMailPreviewLoading, setIsMailPreviewLoading] = useState(false);

  const [isMailsLoading, setIsMailsLoading] = useState(true);
  const [isReloadingMails, setIsReloadingMails] = useState(false);
  const [selectedMailItems, setSelectedMailItems] = useState([]);
  const [showDeleteMailItemsConfirm, setShowDeleteMailItemsConfirm] = useState(false);
  const [showDeleteCommunicationsConfirm, setShowDeleteCommunicationsConfirm] = useState(false);
  const [isReGettingTemplate, setIsReGettingTemplate] = useState(false);
  const [reGetTemplateResults, setReGetTemplateResults] = useState(null);
  const [isEnablingMails, setIsEnablingMails] = useState(false);
  const [isSendingToReview, setIsSendingToReview] = useState(false);
  const [onlyLateOrUnsent, setOnlyLateOrUnsent] = useState(true);

  const fetchMails = async ({ silent = false, reload = false } = {}) => {
    if (reload) {
      setIsReloadingMails(true);
    } else if (!silent) {
      setIsMailsLoading(true);
    }
    try {
      const response = await mailsService.fetchMailsSummary({
        filters: mailFilters,
      });
      const data = response || [];

      setMails(data);
      setFilteredMails(data);
    } catch (err) {
      if (err?.response?.status === 404) {
        setMails([]);
        setFilteredMails([]);
      } else {
        console.error(err);
      }
    } finally {
      if (reload) {
        setIsReloadingMails(false);
      } else if (!silent) {
        setIsMailsLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchMails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const skipInitialGroupsRefetchRef = useRef(true);

  useEffect(() => {
    if (skipInitialGroupsRefetchRef.current) {
      skipInitialGroupsRefetchRef.current = false;
      return;
    }
    if (mailComunication || viewMode !== "list") return;

    fetchMails({ reload: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mailFilters]);

  const agregarValor = (nuevo) => {
    setLista((prev) => {
      const existe = prev.some((item) => item.id === nuevo.id);

      if (existe) {
        return prev.filter((item) => item.id !== nuevo.id);
      }

      return [...prev, nuevo];
    });
  };

  useEffect(() => {
    const fetchLeads = async () => {
      const data = await leadsService.myLeads();
      setLeads(data);
    };

    fetchLeads().catch((err) => {
      console.error(err);
      setNewMailError("Error al cargar leads. Intenta recargar la página.");
    });
  }, []);
  const loadingNewMailModal = !leads;

  const selectedGroupRows = useMemo(() => {
    const selectedGroupIds = new Set(lista.map((row) => row.id));
    return mails.filter((row) => selectedGroupIds.has(row.id));
  }, [lista, mails]);

  const communicationMailsCount = useMemo(
    () =>
      selectedGroupRows.reduce(
        (total, row) => total + (row.mails_count ?? 0),
        0,
      ),
    [selectedGroupRows],
  );

  const queueableMailsCount = useMemo(
    () =>
      selectedGroupRows.reduce(
        (total, row) => total + (row.queueable_mails_count ?? 0),
        0,
      ),
    [selectedGroupRows],
  );

  const reviewMailsCount = useMemo(
    () =>
      onlyLateOrUnsent
        ? selectedGroupRows.reduce(
            (total, row) => total + (row.late_or_unsent_mails_count ?? 0),
            0,
          )
        : queueableMailsCount,
    [selectedGroupRows, onlyLateOrUnsent, queueableMailsCount],
  );

  function sendMailsToReview() {
    if (isSendingToReview) return;

    const templateGroupIds = lista.map((row) => row.id);
    if (templateGroupIds.length > 0 && reviewMailsCount > 0) {
      setIsSendingToReview(true);
      mailsService
        .sendGroupsToReview(templateGroupIds, mailFilters, { onlyLateOrUnsent })
        .then(async () => {
          await fetchMails({ silent: true });
          setViewMode("review");
        })
        .catch((err) => console.error(err))
        .finally(() => setIsSendingToReview(false));
    }

    deselectAllMails();
  }

  const deselectAllMails = () => {
    setLista([]);
  };

  const toggleMailItemSelection = (mailItem) => {
    setSelectedMailItems((prev) => {
      const exists = prev.some((item) => item.id === mailItem.id);
      return exists ? prev.filter((item) => item.id !== mailItem.id) : [...prev, mailItem];
    });
  };

  const sendSelectedMailItemsToReview = () => {
    const mailIds = selectedMailItems.map((item) => item.id);
    if (mailIds.length === 0 || isSendingToReview) return;

    setIsSendingToReview(true);
    mailsService
      .sendToReview({ mail_id: mailIds })
      .then(() => {
        exitMailComunication();
        setViewMode("review");
      })
      .catch((err) => console.error(err))
      .finally(() => setIsSendingToReview(false));
    setSelectedMailItems([]);
  };

  const reGetTemplateForSelectedMailItems = async () => {
    const mailIds = selectedMailItems.map((item) => item.id);
    if (mailIds.length === 0) return;

    setIsReGettingTemplate(true);
    try {
      const response = await mailsService.reGetTemplate(mailIds);
      const results = Array.isArray(response?.data) ? response.data : [];

      const resultsById = new Map(results.map((result) => [result.id, result]));

      setMailItems((prev) => {
        // Si el usuario salió del modo comunicación mientras el request estaba
        // en vuelo, prev es null: no re-entramos con una lista vacía.
        if (prev === null) return prev;
        return prev.map((mailItem) => {
          const result = resultsById.get(mailItem.id);
          if (!result?.ok) return mailItem;
          return {
            ...mailItem,
            subject: result.subject,
            body: result.body,
            active: result.status,
          };
        });
      });

      setReGetTemplateResults(results);
      setSelectedMailItems([]);
    } catch (err) {
      console.error(err);
    } finally {
      setIsReGettingTemplate(false);
    }
  };

  const enableSelectedMailItems = async () => {
    const mailIdsToEnable = selectedMailItems
      .filter(mailNeedsEnabling)
      .map((item) => item.id);
    if (mailIdsToEnable.length === 0) return;

    setIsEnablingMails(true);
    try {
      const response = await mailsService.enableMails(mailIdsToEnable);
      const results = Array.isArray(response?.data) ? response.data : [];
      const changesById = new Map(
        results
          .filter((result) => result.ok)
          .map((result) => [result.id, buildEnabledMailChanges(result)]),
      );

      setMailItems((prev) => {
        if (prev === null) return prev;
        return prev.map((mailItem) =>
          changesById.has(mailItem.id)
            ? { ...mailItem, ...changesById.get(mailItem.id) }
            : mailItem,
        );
      });
      setSelectedMailItems([]);

      const summaryWarning = buildMailEnableSummaryWarning(results);
      if (summaryWarning) setNewMailError(summaryWarning);
    } catch (err) {
      console.error(err);
    } finally {
      setIsEnablingMails(false);
    }
  };

  const deleteSelectedCommunications = async () => {
    const templateGroupIds = lista.map((row) => row.id);

    try {
      await mailsService.deleteCommunicationsByGroups(
        templateGroupIds,
        mailFilters,
      );
    } catch (err) {
      console.error(err);
      setNewMailError(
        "No se pudieron eliminar las comunicaciones seleccionadas.",
      );
    }

    await fetchMails({ silent: true });
    deselectAllMails();
    setShowDeleteCommunicationsConfirm(false);
  };

  const deleteSelectedMailItems = async () => {
    const idsToDelete = selectedMailItems.map((item) => item.id);
    await Promise.all(idsToDelete.map((id) => mailsService.deleteMail(id)));
    setMailItems((prev) => (prev ?? []).filter((item) => !idsToDelete.includes(item.id)));
    setSelectedMailItems([]);
    setShowDeleteMailItemsConfirm(false);
  };

  const handleMailCreated = async () => {
    await fetchMails({ silent: true });
    if (selectedTemplateGroup?.id) {
      await setMailData(selectedTemplateGroup.id);
    }
  };

  const handleDeleteMailFromPreview = async (mailId) => {
    await mailsService.deleteMail(mailId);
    setMailItems((prev) =>
      prev === null ? prev : prev.filter((item) => item.id !== mailId),
    );
    setErrorRows((prev) => prev.filter((row) => row.mail_id !== mailId));
    setSelectedMailItems((prev) => prev.filter((item) => item.id !== mailId));
    setShowMailInfo(false);
  };

  const handleEnableMailFromPreview = async (mailId) => {
    const response = await mailsService.enableMails([mailId]);
    const results = Array.isArray(response?.data) ? response.data : [];
    const result = results.find((item) => item.id === mailId);
    if (!result?.ok) return result;

    const changes = buildEnabledMailChanges(result);

    setMailItems((prev) =>
      prev === null
        ? prev
        : prev.map((item) =>
            item.id === mailId ? { ...item, ...changes } : item,
          ),
    );
    setErrorRows((prev) => prev.filter((row) => row.mail_id !== mailId));
    setLateralModalData((prev) =>
      prev?.id === mailId ? { ...prev, ...changes } : prev,
    );
    setSelectedMailItems((prev) =>
      prev.map((item) =>
        item.id === mailId ? { ...item, ...changes } : item,
      ),
    );

    if (result.chain) {
      fetchRenderedMailIntoPreview(mailId, changes.subject);
    }

    return result;
  };

  const handleUpdateMailFromPreview = async (mailId, changes) => {
    const updatedMail = await mailsService.updateMail(mailId, changes);
    const appliedChanges = { ...changes };

    ["status", "status_response", "send_number", "subject"].forEach((field) => {
      if (updatedMail?.[field] !== undefined) {
        appliedChanges[field] = updatedMail[field];
      }
    });

    setMailItems((prev) =>
      prev === null
        ? prev
        : prev.map((item) =>
            item.id === mailId ? { ...item, ...appliedChanges } : item,
          ),
    );
    setErrorRows((prev) =>
      prev.map((row) =>
        row.mail_id === mailId ? { ...row, ...appliedChanges } : row,
      ),
    );
    setLateralModalData((prev) =>
      prev?.id === mailId ? { ...prev, ...appliedChanges } : prev,
    );

    if (changes.send_number !== undefined) {
      fetchRenderedMailIntoPreview(mailId, appliedChanges.subject);
    }
  };

  const filterFromViewMode =
    viewMode === "review" ? "op2" : viewMode === "errors" ? "op3" : "op1";

  const handleFilterChange = (value) => {
    const nextViewMode =
      value === "op2" ? "review" : value === "op3" ? "errors" : "list";

    if (nextViewMode !== viewMode) {
      setMailFilters(EMPTY_MAIL_FILTERS);
    }
    setViewMode(nextViewMode);
  };

  function handleSearch(term) {
    if (viewMode === "errors") {
      setErrorsSearchTerm(term.trim());
      return;
    }

    if (mailComunication) {
      setMailItemsSearchTerm(term.trim());
      return;
    }

    const tokens = normalizeSearchText(term).split(/\s+/).filter(Boolean);

    if (!tokens.length) {
      setFilteredMails(mails);
      return;
    }

    setFilteredMails(
      mails.filter((row) =>
        matchesEveryToken(getTemplateGroupSearchText(row), tokens),
      ),
    );
  }

  const mailComunication = mailItems !== null;

  const reGetResults = reGetTemplateResults ?? [];
  const updatedReGetResults = reGetResults.filter(isReGetTemplateUpdated);
  const unchangedReGetResults = reGetResults.filter(
    (result) => result.ok && !result.updated,
  );
  const failedReGetResults = reGetResults.filter((result) => !result.ok);

  const fetchMailItemsFirstPage = async (templateGroupId, searchTerm) => {
    const generation = ++mailItemsQueryRef.current;
    setHasMoreMailItems(false);
    setIsLoadingMoreMailItems(false);
    setIsLoadingMailItems(true);

    try {
      const response = await mailsService.fetchTemplateGroupMailsSummary(
        templateGroupId,
        { page: 1, search: searchTerm, filters: mailFilters },
      );
      if (generation !== mailItemsQueryRef.current) return;

      const nextMailItems = Array.isArray(response)
        ? response
        : Array.isArray(response.results)
          ? response.results
          : [];

      mailItemsGroupIdRef.current = templateGroupId;
      mailItemsPageRef.current = 1;
      setHasMoreMailItems(Boolean(!Array.isArray(response) && response?.next));
      setMailItems(nextMailItems);
    } catch (error) {
      if (generation !== mailItemsQueryRef.current) return;
      console.error("Error fetching template group mails:", error);

      setMailItems([]);
    } finally {
      if (generation === mailItemsQueryRef.current) setIsLoadingMailItems(false);
    }
  };

  const setMailData = async (templateID) => {
    const groupRow = filteredMails.find((r) => r.id === templateID) ?? null;
    setSelectedTemplateGroup(groupRow);
    setMailItemsSearchTerm("");

    await fetchMailItemsFirstPage(templateID, "");
  };

  // Cuando cambia el término de búsqueda dentro de un template group se
  // vuelve a pedir la primera página filtrada en el backend (la lista es
  // paginada, filtrar solo lo cargado dejaría resultados afuera).
  useEffect(() => {
    if (!mailComunication) return;

    const templateGroupId = mailItemsGroupIdRef.current;
    if (!templateGroupId) return;

    fetchMailItemsFirstPage(templateGroupId, mailItemsSearchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mailItemsSearchTerm, mailFilters]);

  const fetchErrorsFirstPage = async () => {
    const generation = ++errorsQueryRef.current;
    setHasMoreErrors(false);
    setIsLoadingMoreErrors(false);
    setErrorsLoadFailed(false);
    setIsLoadingErrors(true);

    try {
      const response = await mailsService.fetchSendErrors({
        page: 1,
        search: errorsSearchTerm,
        filters: { responsibleId: mailFilters.responsibleId },
      });
      if (generation !== errorsQueryRef.current) return;

      errorsPageRef.current = 1;
      setErrorRows(Array.isArray(response?.results) ? response.results : []);
      setHasMoreErrors(Boolean(response?.next));
    } catch (error) {
      if (generation !== errorsQueryRef.current) return;
      console.error("Error fetching send errors:", error);

      setErrorRows([]);
      setErrorsLoadFailed(true);
    } finally {
      if (generation === errorsQueryRef.current) setIsLoadingErrors(false);
    }
  };

  const loadMoreErrors = async () => {
    if (!hasMoreErrors || isLoadingMoreErrors) return;

    const generation = errorsQueryRef.current;
    setIsLoadingMoreErrors(true);

    try {
      const response = await mailsService.fetchSendErrors({
        page: errorsPageRef.current + 1,
        search: errorsSearchTerm,
        filters: { responsibleId: mailFilters.responsibleId },
      });
      if (generation !== errorsQueryRef.current) return;

      const newErrorRows = Array.isArray(response?.results)
        ? response.results
        : [];

      errorsPageRef.current += 1;
      setHasMoreErrors(Boolean(response?.next));
      setErrorRows((prev) => {
        const existingIds = new Set(prev.map((row) => row.id));
        return [
          ...prev,
          ...newErrorRows.filter((row) => !existingIds.has(row.id)),
        ];
      });
    } catch (error) {
      if (generation === errorsQueryRef.current) {
        console.error("Error fetching more send errors:", error);
      }
    } finally {
      if (generation === errorsQueryRef.current) {
        setIsLoadingMoreErrors(false);
      }
    }
  };

  useEffect(() => {
    if (viewMode !== "errors") return;

    fetchErrorsFirstPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, errorsSearchTerm, mailFilters]);

  useEffect(() => {
    if (viewMode === "errors") return;

    errorsQueryRef.current += 1;
    setErrorsSearchTerm("");
    setIsLoadingErrors(false);
    setIsLoadingMoreErrors(false);
    setHasMoreErrors(false);
  }, [viewMode]);

  useEffect(() => {
    const loadedRows = [
      ...(leads ?? []),
      ...(mailItems ?? []),
      ...errorRows,
    ];
    let hasNewResponsibles = false;

    loadedRows.forEach((row) => {
      const responsible = row?.responsible;
      if (!responsible?.id || seenResponsiblesRef.current.has(responsible.id)) {
        return;
      }
      seenResponsiblesRef.current.set(responsible.id, {
        id: responsible.id,
        name: responsible.name || responsible.email || "Sin casilla",
      });
      hasNewResponsibles = true;
    });

    if (hasNewResponsibles) {
      setMailAccountOptions(Array.from(seenResponsiblesRef.current.values()));
    }
  }, [leads, mailItems, errorRows]);

  const fetchRenderedMailIntoPreview = async (mailId, fallbackSubject) => {
    const generation = ++mailPreviewQueryRef.current;
    setIsMailPreviewLoading(true);

    try {
      const renderedMail = await mailsService.fetchMailRendered(mailId);
      if (generation !== mailPreviewQueryRef.current) return;

      setLateralModalData((prev) => ({
        ...prev,
        subject: renderedMail?.subject ?? fallbackSubject,
        body: renderedMail?.body ?? "",
      }));
    } catch (error) {
      if (generation !== mailPreviewQueryRef.current) return;
      console.error("Error fetching rendered mail:", error);

      setShowMailInfo(false);
      setNewMailError("No se pudo cargar el detalle del mail.");
    } finally {
      if (generation === mailPreviewQueryRef.current) {
        setIsMailPreviewLoading(false);
      }
    }
  };

  const openMailItemPreview = async (mailItem) => {
    setNewMailError("");
    handleLateralModal({ ...mailItem, body: "" });
    await fetchRenderedMailIntoPreview(mailItem.id, mailItem.subject);
  };

  const openMailFromErrorRow = async (errorRow) => {
    setNewMailError("");
    handleLateralModal({
      id: errorRow.mail_id,
      subject: errorRow.subject,
      body: "",
      status: errorRow.status,
      status_response: errorRow.status_response,
      rejected: errorRow.rejected,
      send_error: errorRow.error_message,
      send_error_date_approved: errorRow.date_approved,
      last_send: errorRow.last_send,
      lead: errorRow.lead,
      mail_account: errorRow.mail_account,
      responsible: errorRow.responsible,
      template_group: errorRow.template_group,
    });
    await fetchRenderedMailIntoPreview(errorRow.mail_id, errorRow.subject);
  };

  const loadMoreMailItems = async () => {
    const templateGroupId = mailItemsGroupIdRef.current;
    if (!templateGroupId || !hasMoreMailItems || isLoadingMoreMailItems) return;

    const generation = mailItemsQueryRef.current;
    setIsLoadingMoreMailItems(true);

    try {
      const response = await mailsService.fetchTemplateGroupMailsSummary(
        templateGroupId,
        {
          page: mailItemsPageRef.current + 1,
          search: mailItemsSearchTerm,
          filters: mailFilters,
        },
      );
      if (generation !== mailItemsQueryRef.current) return;

      const newMailItems = Array.isArray(response?.results)
        ? response.results
        : [];

      mailItemsPageRef.current += 1;
      setHasMoreMailItems(Boolean(response?.next));
      setMailItems((prev) => {
        if (prev === null) return prev;
        const existingIds = new Set(prev.map((item) => item.id));
        return [
          ...prev,
          ...newMailItems.filter((item) => !existingIds.has(item.id)),
        ];
      });
    } catch (error) {
      if (generation === mailItemsQueryRef.current) {
        console.error("Error fetching more template group mails:", error);
      }
    } finally {
      if (generation === mailItemsQueryRef.current) {
        setIsLoadingMoreMailItems(false);
      }
    }
  };

  const exitMailComunication = () => {
    mailItemsQueryRef.current += 1;
    mailItemsGroupIdRef.current = null;
    setMailItems(null);
    setSelectedTemplateGroup(null);
    setHasMoreMailItems(false);
    setIsLoadingMoreMailItems(false);
    setIsLoadingMailItems(false);
    // El buscador se limpia al cambiar de vista (la toolbar remonta el input),
    // así que el listado de grupos vuelve a mostrarse completo.
    setMailItemsSearchTerm("");
    setFilteredMails(mails);
  };

  const isNearBottom = (scrollElement) => {
    if (!scrollElement) return false;
    const distanceToBottom =
      scrollElement.scrollHeight -
      scrollElement.scrollTop -
      scrollElement.clientHeight;
    return distanceToBottom <= 300;
  };

  const handleMailsScroll = (event) => {
    if (!mailsScrollStateRef.current.canLoadMore) return;
    if (!isNearBottom(event.currentTarget)) return;
    mailsScrollStateRef.current.loadMore();
  };

  // El scroll puede ocurrir en el contenedor interno (.mails1) o en la
  // ventana según el alto del contenido, igual que en Database: se escuchan
  // ambos y el estado se lee desde un ref para no re-suscribir en cada render.
  mailsScrollStateRef.current = {
    canLoadMore:
      (mailComunication && hasMoreMailItems && !isLoadingMoreMailItems) ||
      (viewMode === "errors" && hasMoreErrors && !isLoadingMoreErrors),
    loadMore: viewMode === "errors" ? loadMoreErrors : loadMoreMailItems,
  };

  useEffect(() => {
    const onWindowScroll = () => {
      if (!isNearBottom(document.documentElement)) return;

      const scrollState = mailsScrollStateRef.current;
      if (scrollState.canLoadMore) scrollState.loadMore();
    };

    window.addEventListener("scroll", onWindowScroll, { passive: true });
    return () => window.removeEventListener("scroll", onWindowScroll);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const tableHeaders = mailComunication
    ? [
        { label: "Cuenta", column: "1" },
        { label: "Lead", column: "3" },
        { label: "Asunto", column: "5" },
        { label: "#", column: "7" },
        { label: "Estado", column: "9" },
        { label: "Respondido", column: "11" },
        { label: "Última fecha", column: "13" },
        { label: "Responsable", column: "15" },
      ]
    : [
        { label: "Cuenta", column: "1" },
        { label: "Template", column: "3" },
        { label: "Mails Activos", column: "5" },
        { label: "Estado", column: "7" },
        { label: "Tasa de Respuesta", column: "9" },
      ];

  const handleLateralModal = (mailItemData) => {
    setLateralModalData(mailItemData);
    setShowMailInfo(true);
  };

  if (isMailsLoading) {
    return <LoadingScreen />;
  }

  if (isSendingToReview) {
    return (
      <LoadingScreen
        mainText="Mandando a revisar..."
        subText="Estamos preparando los mails seleccionados"
      />
    );
  }

  if (isLoadingMailItems && !mailComunication) {
    return (
      <LoadingScreen
        mainText="Cargando mails..."
        subText="Puede demorar un rato si el template tiene muchos mails"
      />
    );
  }

  return (
    <div className={styles.mails}>
      <div
        className={styles.mails1}
        data-testid="mails-scroll-container"
        style={viewMode === "review" ? { overflowY: "hidden" } : undefined}
        onScroll={handleMailsScroll}
        onClick={lista.length > 0 || selectedMailItems.length > 0 ? () => { deselectAllMails(); setSelectedMailItems([]); } : undefined}
      >
        <div className={styles.mailsInner}>
          <div className={styles.frameParent}>
            <div
              className={styles.mailsWrapper}
              onClick={(event) => event.stopPropagation()}
            >
              {mailComunication && (
                <IconButton
                  tone="neutral"
                  size="md"
                  icon={BackIcon}
                  onClick={(event) => {
                    event.stopPropagation();
                    exitMailComunication();
                  }}
                  aria-label="Volver"
                />
              )}
              <div className={styles.div}>Mails</div>
            </div>

            <div style={{ display: "contents" }} onClick={(event) => event.stopPropagation()}>
              <MailsToolbar
                onSearchChange={handleSearch}
                onFilterChange={handleFilterChange}
                onOpenReview={() => handleFilterChange("op2")}
                selectedFilter={filterFromViewMode}
                mailComunicationState={mailComunication}
                appliedFilters={mailFilters}
                mailAccountOptions={mailAccountOptions}
                onApplyFilters={setMailFilters}
                onNewMail={() => {
                  deselectAllMails();
                  setSelectedMailItems([]);
                  setNewMailModalState((prev) => !prev);
                }}
                disabledNewMail={loadingNewMailModal}
              />
            </div>

            {viewMode === "review" ? (
              <div className={styles.reviewScreen}>
                <MailFlyer responsibleFilterId={mailFilters.responsibleId} />
              </div>
            ) : viewMode === "errors" ? (
              isLoadingErrors ? (
                <LoadingScreen
                  embedded
                  mainText="Cargando mails con errores..."
                  subText=""
                />
              ) : errorsLoadFailed ? (
                <div className={styles.errorsLoadFailed}>
                  <span>No se pudieron cargar los mails con errores.</span>
                  <Button
                    variant="primary"
                    tone="blue"
                    height={40}
                    onClick={() => fetchErrorsFirstPage()}
                  >
                    Reintentar
                  </Button>
                </div>
              ) : (
                <>
                  <MailErrorsTable
                    rows={errorRows}
                    onOpenMail={openMailFromErrorRow}
                    hasActiveSearch={
                      Boolean(errorsSearchTerm) ||
                      mailFilters.responsibleId != null
                    }
                  />
                  {isLoadingMoreErrors && (
                    <div className={styles.smallSpinner}>
                      Cargando más mails...
                    </div>
                  )}
                </>
              )
            ) : (
              <>
                {fetchingMy ? (
                  <div className={styles.smallSpinner}>
                    Actualizando “My mails”...
                  </div>
                ) : isLoadingMailItems || isReloadingMails ? (
                  <LoadingScreen embedded mainText="Cargando mails..." subText="" />
                ) : (
                  <>
                    <MailsTable
                      rows={filteredMails}
                      onCardSelected={mailComunication ? toggleMailItemSelection : agregarValor}
                      selectedMails={mailComunication ? selectedMailItems : lista}
                      selectComunication={setMailData}
                      headers={tableHeaders}
                      mailComunication={mailComunication}
                      mailItems={mailItems}
                      handleLateralModal={openMailItemPreview}
                    />
                    {isLoadingMoreMailItems && (
                      <div className={styles.smallSpinner}>
                        Cargando más mails...
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      {viewMode !== "review" && (
        <>
          {lista.length > 0 && (
            <FloatingModal bottom={56}>
              <div className={styles.flotingModalContainer}>
                <div className={styles.textLine}>
                  <span className={styles.text}>Mandarás</span>
                  <span className={styles.textError}>
                    {reviewMailsCount} mail
                    {reviewMailsCount !== 1 && "s"}
                  </span>
                  <span className={styles.text}>para revisar</span>
                </div>
                <div className={styles.lateToggle}>
                  <Toggle
                    checked={onlyLateOrUnsent}
                    onChange={setOnlyLateOrUnsent}
                    ariaLabel="Solo atrasados y nuevos"
                  />
                  <span>Solo atrasados y nuevos</span>
                </div>
              </div>

              <div className={styles.actions}>
                <HoverRocketButton
                  tone="green"
                  width={178}
                  height={56}
                  disabled={reviewMailsCount === 0}
                  onClick={sendMailsToReview}
                >
                  Mandar a revisar
                </HoverRocketButton>

                <IconButton
                  tone="danger"
                  size="md"
                  iconSize={32}
                  icon={<TrashIcon className={styles.trashIconMails} />}
                  aria-label="Eliminar comunicaciones seleccionadas"
                  onClick={() => setShowDeleteCommunicationsConfirm(true)}
                />
              </div>
            </FloatingModal>
          )}

          {showDeleteCommunicationsConfirm && (
            <ConfirmDeleteModal
              title={`¿Eliminar ${lista.length} ${lista.length !== 1 ? "comunicaciones" : "comunicación"}?`}
              description={`Esta acción eliminará ${communicationMailsCount} mail${communicationMailsCount !== 1 ? "s" : ""} en total y no tiene vuelta atrás.`}
              onConfirm={deleteSelectedCommunications}
              onClose={() => setShowDeleteCommunicationsConfirm(false)}
            />
          )}

          {reGetTemplateResults && (
            <FloatingModal>
              <div className={styles.reGetResults}>
                <span className={styles.reGetResultsTitle}>
                  {updatedReGetResults.length} mail
                  {updatedReGetResults.length !== 1 ? "s" : ""} actualizado
                  {updatedReGetResults.length !== 1 ? "s" : ""}
                </span>
                {unchangedReGetResults.length > 0 && (
                  <span className={styles.reGetResultsUnchanged}>
                    {unchangedReGetResults.length} sin cambios
                  </span>
                )}
                {failedReGetResults.length > 0 && (
                  <span className={styles.reGetResultsFailed}>
                    {failedReGetResults.length} no se{" "}
                    {failedReGetResults.length !== 1 ? "pudieron" : "pudo"}{" "}
                    recalcular
                  </span>
                )}
              </div>
              <button
                type="button"
                className={styles.closePopupButton}
                onClick={() => setReGetTemplateResults(null)}
              >
                Ok
              </button>
            </FloatingModal>
          )}

          {newMailError && (
            <FloatingModal
              direction="column"
              width={350}
              height={125}
              bottom={250}
              gap={25}
            >
              <div className={styles.newMailErrorContainer}>
                {newMailError}
              </div>
              <div
                type="button"
                className={styles.closePopupButton}
                onClick={() => setNewMailError("")}
              >
                Ok
              </div>
            </FloatingModal>
          )}

          {newMailModalState && (
            <NewMailModal
              leads={leads}
              onClose={() => setNewMailModalState(false)}
              onMailCreated={handleMailCreated}
              onNewMailError={(errorMsg) => setNewMailError(errorMsg)}
            />
          )}

          {mailComunication && selectedMailItems.length > 0 && (
            <FloatingModal width={700} bottom={56}>
              <div className={styles.actions}>
                <Button
                  variant="primary"
                  tone="blue"
                  height={50}
                  loading={isReGettingTemplate}
                  onClick={reGetTemplateForSelectedMailItems}
                  style={{ minWidth: 160 }}
                >
                  Re Get template
                </Button>
                <Button
                  variant="primary"
                  tone="blue"
                  height={50}
                  loading={isEnablingMails}
                  disabled={!selectedMailItems.some(mailNeedsEnabling)}
                  onClick={enableSelectedMailItems}
                  style={{ minWidth: 120 }}
                >
                  Habilitar
                </Button>
                <HoverRocketButton
                  tone="green"
                  width={178}
                  height={50}
                  onClick={sendSelectedMailItemsToReview}
                >
                  Mandar a revisar
                </HoverRocketButton>
                <Button
                  tone="red"
                  height={50}
                  iconLeft={<TrashIcon className={styles.trashIconMails} />}
                  style={{ minWidth: 140 }}
                  onClick={() => setShowDeleteMailItemsConfirm(true)}
                >
                  Eliminar
                </Button>
              </div>
            </FloatingModal>
          )}

          {showDeleteMailItemsConfirm && (
            <ConfirmDeleteModal
              title={`¿Eliminar ${selectedMailItems.length} mail${selectedMailItems.length !== 1 ? "s" : ""}?`}
              description="Esta acción no tiene vuelta atrás. Si alguno de estos mails está esperando revisión o tiene una comunicación activa, también será eliminado."
              onConfirm={deleteSelectedMailItems}
              onClose={() => setShowDeleteMailItemsConfirm(false)}
            />
          )}
        </>
      )}

      {showMailInfo && (
        <MailPreview
          data={lateralModalData}
          isLoading={isMailPreviewLoading}
          templateGroupId={
            lateralModalData?.template_group?.id ?? selectedTemplateGroup?.id
          }
          onClose={() => setShowMailInfo(false)}
          onDeleteMail={handleDeleteMailFromPreview}
          onUpdateMail={handleUpdateMailFromPreview}
          onEnableMail={handleEnableMailFromPreview}
        />
      )}
    </div>
  );
};

export default MailsPage;
